import React from "react";
import { useNavigate } from "react-router-dom";
import { FiChevronRight } from "react-icons/fi";

function resolveField(p, ...keys) {
  for (const k of keys) {
    if (p[k] !== undefined && p[k] !== null && p[k] !== "") return p[k];
  }
  return null;
}

function buildAddress(p) {
  const num = p.street_address_number || "";
  const name = p.street_address_name || "";
  const combined = `${num} ${name}`.trim();
  return combined || p.address || p.location || "";
}

function buildPrice(p) {
  const display = resolveField(p, "price_display");
  if (display && p.price_display_checked) return display;
  const raw = resolveField(p, "price");
  if (raw) return `₦${Number(raw).toLocaleString()}`;
  return "Price on request";
}

const PropertyCard = ({ property, onClick }) => {
  const image = resolveField(property, "frontPageImg", "image", "thumbnail");
  const agencyLogo = property.lead_agent?.agencyLogo || null;
  const agencyName = property.lead_agent?.name || "La Rosa";
  const name = property.newDevelopment?.projectName || resolveField(property, "name", "title", "street_address_name") || "Property";
  const suburb = resolveField(property, "suburb", "location", "city") || "";
  const address = buildAddress(property);
  const price = buildPrice(property);

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden cursor-pointer group border-[1.5px] border-[#EAEAEA]"
      onClick={onClick}
    >
      <div className="relative overflow-hidden w-full h-[280px]">
        {image ? (
          <img
            src={image}
            alt={name}
            loading="lazy"
            className="w-full h-[280px] object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-[280px] bg-gray-200 flex items-center justify-center">
            <span className="text-gray-400 text-sm">No image</span>
          </div>
        )}
        <div className="absolute top-0 left-0 right-0 flex justify-center items-center py-3" style={{ backgroundColor: property.primary_color || "#373639" }}>
          {agencyLogo ? (
            <img
              src={agencyLogo}
              alt={agencyName}
              loading="lazy"
              className="h-8 w-auto object-contain"
            />
          ) : (
            <span className="text-white text-xs font-semibold tracking-widest uppercase">
              {agencyName}
            </span>
          )}
        </div>
        <div className="absolute bottom-0 left-0 right-0 px-4 py-3 bg-gradient-to-t from-black via-black/60 to-transparent">
          <p className="text-white text-2xl line-clamp-2">{name}</p>
        </div>
      </div>

      <div className="px-5 py-4">
        <p className="text-xl font-semibold text-gray-900">{suburb}</p>
        <p className="text-base text-gray-500 py-1.5 line-clamp-1">{address}</p>
        <p className="text-base font-bold text-gray-900">
          From <span>{price}</span>
        </p>

        <div className="bg-[#EAEAEA] w-full h-[1px] my-3" />

        <div className="mt-4 flex justify-end">
          <button className="flex items-center gap-1 text-sm text-[#097FE6] hover:text-[#097FE6]/80 font-medium transition-colors">
            Details <FiChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

const SkeletonCard = () => (
  <div className="bg-white rounded-2xl overflow-hidden border-[1.5px] border-[#EAEAEA] animate-pulse">
    <div className="w-full h-[280px] bg-gray-200" />
    <div className="px-5 py-4 space-y-3">
      <div className="h-5 bg-gray-200 rounded w-2/3" />
      <div className="h-4 bg-gray-200 rounded w-full" />
      <div className="h-4 bg-gray-200 rounded w-1/3" />
    </div>
  </div>
);

const MapListView = ({ onBack, activeCategory, properties = [], loading = false, error = null }) => {
  const navigate = useNavigate();

  return (
    <div className="container mx-auto">
      <button
        onClick={onBack}
        className="text-md text-gray-500 hover:text-blue-600 transition-colors flex items-center gap-1"
      >
        Back to Homepage
      </button>

      <h2 className="text-2xl font-bold py-3">{activeCategory} for sale in Map area</h2>

      {!loading && !error && (
        <p className="text-md text-gray-500 mt-1 mb-6">{properties.length} properties</p>
      )}

      {error && (
        <p className="text-red-500 text-sm mb-6">{error}</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
          : properties.length > 0
          ? properties.map((p) => (
              <PropertyCard
                key={p.id || p._id}
                property={p}
                onClick={() => navigate(`/new-homes/development/${p.id || p._id}`)}
              />
            ))
          : !error && (
              <p className="col-span-3 text-center text-gray-400 py-16">
                No properties found.
              </p>
            )}
      </div>
    </div>
  );
};

export default MapListView;
